/* frontend/src/pages/ContactList.js */
import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MessageCircle, Check, Clock, X } from 'lucide-react';
import api from '../api';
import { PageHeader } from '../components/common';

// Shadcn UI Components
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Typography } from '../components/ui/typography';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableHeaderRow
} from '../components/ui/table';
import { Spinner } from '../components/ui/spinner';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

export default function ContactList() {
  const { t } = useTranslation();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('');

  // Carrega contatos
  useEffect(() => {
    const loadContacts = async () => {
      try {
        setLoading(true);
        const response = await api.get('/contatos');
        setContacts(response.data.sort((a, b) => new Date(b.date) - new Date(a.date)));
      } catch (error) {
        console.error('Erro ao carregar contatos:', error);
        setError(t('contactList.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    
    loadContacts();
  }, []);

  // Abre modal de detalhe
  const openContactDetail = (contact) => {
    setSelectedContact(contact);
    setNotes(contact.notes || '');
    setStatus(contact.status || 'new');
  };

  // Salva atualização de status e notas
  const handleSaveContactUpdate = async () => {
    try {
      await api.put(`/contatos/${selectedContact._id}`, {
        notes,
        status
      });
      
      // Atualiza o contato na lista
      setContacts(contacts.map(contact => 
        contact._id === selectedContact._id 
          ? { ...contact, notes, status } 
          : contact
      ));
      
      // Fecha modal
      setSelectedContact(null);
    } catch (error) {
      console.error('Erro ao atualizar contato:', error);
    }
  };

  // Status badges
  const getStatusBadge = (status) => {
    switch(status) {
      case 'new':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">{t('contactList.status.new')}</Badge>;
      case 'contacted':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">{t('contactList.status.contacted')}</Badge>;
      case 'closed':
        return <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">{t('contactList.status.closed')}</Badge>;
      default:
        return <Badge variant="secondary">{t('contactList.status.unknown')}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <PageHeader title={t('contactList.title')} />
        <div className="flex items-center justify-center min-h-[300px]">
          <Spinner className="h-8 w-8" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <PageHeader title={t('contactList.title')} />
        <Card className="p-6 text-center">
          <Typography variant="h3" className="mb-2">{t('contactList.errors.title')}</Typography>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>{t('contactList.errors.tryAgain')}</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <PageHeader 
        title={t('contactList.title')} 
        description={t('contactList.description')}
      />
      
      {contacts.length > 0 ? (
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableHeaderRow className="bg-[#ffa300] text-white">
                  <TableHead className="text-white">{t('contactList.table.headers.date')}</TableHead>
                  <TableHead className="text-white">{t('contactList.table.headers.name')}</TableHead>
                  <TableHead className="text-white">{t('contactList.table.headers.contact')}</TableHead>
                  <TableHead className="text-white">{t('contactList.table.headers.vehicle')}</TableHead>
                  <TableHead className="text-white">{t('contactList.table.headers.status')}</TableHead>
                  <TableHead className="w-[100px] text-white text-right">{t('contactList.table.headers.actions')}</TableHead>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow key={contact._id}>
                    <TableCell className="font-medium">
                      {format(new Date(contact.date), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                    </TableCell>
                    <TableCell>{contact.name}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="flex items-center gap-1 text-xs">
                          <Phone className="h-3 w-3" /> {contact.phone}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Mail className="h-3 w-3" /> {contact.email}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{contact.vehicleInfo || '-'}</TableCell>
                    <TableCell>{getStatusBadge(contact.status)}</TableCell>
                    <TableCell>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openContactDetail(contact)}>
                            <MessageCircle className="h-4 w-4" />
                            <span className="sr-only">{t('contactList.table.actions.viewDetails')}</span>
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[500px]">
                          <DialogHeader>
                            <DialogTitle>{t('contactList.dialog.title')}</DialogTitle>
                            <DialogDescription>
                              {t('contactList.dialog.description', { name: contact.name, date: format(new Date(contact.date), 'dd/MM/yyyy HH:mm', { locale: ptBR }) })}
                            </DialogDescription>
                          </DialogHeader>
                          
                          {selectedContact && (
                            <>
                              <div className="grid gap-4">
                                <div className="space-y-2">
                                  <h4 className="text-sm font-medium">{t('contactList.dialog.clientInfo')}</h4>
                                  <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                      <span className="text-muted-foreground block">{t('contactList.dialog.fields.name')}:</span>
                                      <span>{selectedContact.name}</span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground block">{t('contactList.dialog.fields.phone')}:</span>
                                      <span>{selectedContact.phone}</span>
                                    </div>
                                    <div className="col-span-2">
                                      <span className="text-muted-foreground block">{t('contactList.dialog.fields.email')}:</span>
                                      <span>{selectedContact.email}</span>
                                    </div>
                                  </div>
                                </div>
                                
                                {selectedContact.vehicleId && (
                                  <div className="space-y-2">
                                    <h4 className="text-sm font-medium">{t('contactList.dialog.vehicle')}</h4>
                                    <p className="text-sm">{selectedContact.vehicleInfo}</p>
                                    <Button variant="outline" size="sm" asChild>
                                      <a href={`/admin/veiculos/${selectedContact.vehicleId._id || selectedContact.vehicleId}`} target="_blank" rel="noreferrer">
                                        {t('contactList.dialog.viewVehicle')}
                                      </a>
                                    </Button>
                                  </div>
                                )}
                                
                                {selectedContact.message && (
                                  <div className="space-y-2">
                                    <h4 className="text-sm font-medium">{t('contactList.dialog.message')}</h4>
                                    <p className="text-sm whitespace-pre-wrap">
                                      {selectedContact.message}
                                    </p>
                                  </div>
                                )}
                                
                                <div className="space-y-2">
                                  <h4 className="text-sm font-medium">{t('contactList.dialog.statusLabel')}</h4>
                                  <Select value={status} onValueChange={setStatus}>
                                    <SelectTrigger>
                                      <SelectValue placeholder={t('contactList.dialog.selectStatus')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="new">{t('contactList.status.new')}</SelectItem>
                                      <SelectItem value="contacted">{t('contactList.status.contacted')}</SelectItem>
                                      <SelectItem value="closed">{t('contactList.status.closed')}</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                
                                <div className="space-y-2">
                                  <h4 className="text-sm font-medium">{t('contactList.dialog.notes')}</h4>
                                  <Textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder={t('contactList.dialog.notesPlaceholder')}
                                    rows={3}
                                  />
                                </div>
                              </div>
                              
                              <DialogFooter>
                                <DialogClose asChild>
                                  <Button type="button" variant="secondary">
                                    {t('contactList.dialog.cancel')}
                                  </Button>
                                </DialogClose>
                                <Button type="button" onClick={handleSaveContactUpdate}>
                                  {t('contactList.dialog.save')}
                                </Button>
                              </DialogFooter>
                            </>
                          )}
                        </DialogContent>
                      </Dialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : (
        <Card className="p-6 text-center">
          <Typography variant="h4" className="mb-2">{t('contactList.emptyState.title')}</Typography>
          <p className="text-muted-foreground">
            {t('contactList.emptyState.description')}
          </p>
        </Card>
      )}
    </div>
  );
}
